import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Typography,
  Tag,
  Button,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  message,
  Popconfirm
} from 'antd';
import {
  HistoryOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface AuditLog {
  _id: string;
  action: string;
  endpoint: string;
  method: string;
  ipAddress: string;
  userAgent?: string;
  statusCode?: number;
  timestamp: string;
  details?: any;
  userId?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    companyName?: string;
  };
}

const ActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [filters, setFilters] = useState({
    action: '',
    startDate: '',
    endDate: '',
    statusCode: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      const data = await stationOwnerAPI.getAuditLogs(params);
      setLogs(data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0
      }));
    } catch (error: any) {
      message.error('Loglar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await stationOwnerAPI.getAuditStats();
      setStats(data);
    } catch (error: any) {
      console.error('İstatistikler yüklenemedi:', error);
    }
  };

  const handleViewDetail = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  const handleClearOldLogs = async () => {
    try {
      await stationOwnerAPI.clearOldLogs(90);
      message.success('Eski loglar temizlendi');
      fetchLogs();
      fetchStats();
    } catch (error: any) {
      message.error('Loglar temizlenemedi');
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'blue',
      POST: 'green',
      PUT: 'orange',
      DELETE: 'red',
      PATCH: 'purple'
    };
    return colors[method] || 'default';
  };

  const getStatusCodeColor = (code?: number) => {
    if (!code) return 'default';
    if (code >= 200 && code < 300) return 'success';
    if (code >= 300 && code < 400) return 'warning';
    if (code >= 400) return 'error';
    return 'default';
  };

  const columns = [
    {
      title: 'Tarih/Saat',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm:ss')
    },
    {
      title: 'İşlem',
      dataIndex: 'action',
      key: 'action',
      render: (action: string) => (
        <Tag color="blue">{action}</Tag>
      )
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      ellipsis: true
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      render: (method: string) => (
        <Tag color={getMethodColor(method)}>{method}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'statusCode',
      key: 'statusCode',
      width: 100,
      render: (code?: number) => code ? (
        <Tag color={getStatusCodeColor(code)}>{code}</Tag>
      ) : '-'
    },
    {
      title: 'IP Adresi',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 150
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 100,
      render: (_: any, record: AuditLog) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
          size="small"
        >
          Detay
        </Button>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            Aktivite Logları
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
            Sistem aktivitelerinizi ve değişiklik geçmişinizi görüntüleyin
          </Text>
        </Col>
      </Row>

      {/* İstatistikler */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam İşlem"
                value={stats.totalActions || 0}
                prefix={<HistoryOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Son 24 Saat"
                value={stats.recentActions || 0}
                prefix={<ReloadOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="İşlem Tipleri"
                value={stats.actionsByType?.length || 0}
                prefix={<FilterOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Popconfirm
                title="90 günden eski logları silmek istediğinizden emin misiniz?"
                onConfirm={handleClearOldLogs}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  block
                >
                  Eski Logları Temizle
                </Button>
              </Popconfirm>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtreler */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Input
              placeholder="İşlem ara..."
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Select
              placeholder="Status Code"
              value={filters.statusCode || undefined}
              onChange={(value) => handleFilterChange('statusCode', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="200">200 - OK</Option>
              <Option value="201">201 - Created</Option>
              <Option value="400">400 - Bad Request</Option>
              <Option value="401">401 - Unauthorized</Option>
              <Option value="403">403 - Forbidden</Option>
              <Option value="404">404 - Not Found</Option>
              <Option value="500">500 - Server Error</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange('startDate', dates[0]?.toISOString());
                  handleFilterChange('endDate', dates[1]?.toISOString());
                } else {
                  handleFilterChange('startDate', '');
                  handleFilterChange('endDate', '');
                }
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Log Tablosu */}
      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            responsive: true,
            showTotal: (total) => `Toplam ${total} kayıt`
          }}
          onChange={(pagination) => {
            setPagination(prev => ({
              ...prev,
              page: pagination.current || 1
            }));
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Detay Modal */}
      <Modal
        title="Log Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={isMobile ? '100%' : 800}
      >
        {selectedLog && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Tarih/Saat">
              {dayjs(selectedLog.timestamp).format('DD.MM.YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="İşlem">
              <Tag color="blue">{selectedLog.action}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Endpoint">
              {selectedLog.endpoint}
            </Descriptions.Item>
            <Descriptions.Item label="Method">
              <Tag color={getMethodColor(selectedLog.method)}>
                {selectedLog.method}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status Code">
              {selectedLog.statusCode ? (
                <Tag color={getStatusCodeColor(selectedLog.statusCode)}>
                  {selectedLog.statusCode}
                </Tag>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="IP Adresi">
              {selectedLog.ipAddress}
            </Descriptions.Item>
            {selectedLog.userAgent && (
              <Descriptions.Item label="User Agent">
                {selectedLog.userAgent}
              </Descriptions.Item>
            )}
            {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
              <Descriptions.Item label="Detaylar">
                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default ActivityLogs;

